<template>
  <loader :is-loading="loading" :fullPage="true" color="primary">
    <v-container fluid class="align-start align-content-start fill-height">
      <div class="bs-table-header pt-8">
        <v-row justify="space-between" class="align-center mb-3 px-4">
          <v-col cols="12" class="col-without-top-padding col-without-bottom-padding">
            <h1 class="bs-header-title">
              {{ $t('components.employeeList.title') }}
            </h1>
          </v-col>
          <v-col md="4" class="col-without-top-padding col-without-bottom-padding v-tabs-container">
            <v-tabs
              v-model="tab"
              value="tab"
              hide-slider
            >
              <v-tab
                v-for="item in tabs"
                :key="item.tab"
                :ripple="false"
                class="tabs-text"
                active-class="tabs-text-active"
                @click="$router.push(`/employees/${item.tab}`)"
              >
                {{ $t(`components.employeeList.tabs.${item.tab}`) }}
              </v-tab>
            </v-tabs>
          </v-col>
          <v-col :cols="isGroupCreation ? 2 : 3" class="d-flex align-center col-without-top-padding col-without-bottom-padding">
            <v-text-field
              v-if="!isGroupCreation"
              v-model="dynamicSearch"
              :label="$t('components.employeeList.search')"
              prepend-inner-icon="mdi-magnify"
              clearable
              outlined
              hide-details
              :height="38"
              class="mr-3 bs-search-item"
            ></v-text-field>
            <v-btn
              v-if="!isGroupCreation"
              class="add-bs-button"
              color="#333333"
              :height="40"
              depressed
              @click="addEmployeeClick()"
            >
              <v-icon left>mdi-plus</v-icon>
              {{ $t(`components.employeeList.${getTabAction().tab}.add`) }}
            </v-btn>
            <v-menu left offset-y nudge-bottom="15" v-if="getActiveTab">
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon v-bind="attrs" v-on="on" class="ml-3">
                  <v-icon>mdi-dots-vertical</v-icon>
                </v-btn>
              </template>
              <v-list dense>
                <v-list-item link @click="showImportDialog">
                  <v-list-item-content class="mr-6">
                    <v-list-item-title>{{ $t('global.importCsv') }}</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-icon>
                    <v-icon size="20" color="#ccc">mdi-tray-arrow-up</v-icon>
                  </v-list-item-icon>
                </v-list-item>
                <v-list-item link target="_blank" href="/templates/user_import_template.csv">
                  <v-list-item-content class="mr-6">
                    <v-list-item-title>{{ $t('global.csvTemplate') }}</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-icon>
                    <v-icon size="20" color="#ccc">mdi-tray-arrow-down</v-icon>
                  </v-list-item-icon>
                </v-list-item>
                <v-list-item link @click="exportUsersList">
                  <v-list-item-content class="mr-6">
                    <v-list-item-title>{{ $t('global.exportUsersList') }}</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-icon>
                    <v-icon size="20" color="#ccc">mdi-tray-arrow-down</v-icon>
                  </v-list-item-icon>
                </v-list-item>
              </v-list>
            </v-menu>
          </v-col>
        </v-row>
      </div>
      <employee-group-form v-if="isGroupCreation" :selectedGroup="selectedGroupItem" @groupUpdated="updateGroup" />
      <div v-else class="bs-table-table">
        <v-row no-gutters>
          <v-col class="col-without-top-padding table-color">
            <v-data-table
              class="bs-table elevation-0"
              calculate-widths
              :items="employees"
              :headers="headers"
              :server-items-length="totalEmployees"
              :loading="employeesLoading"
              :multi-sort="false"
              :must-sort="true"
              :single-expand="true"
              :page.sync="page"
              :options.sync="options"
              :items-per-page="itemsPerPage"
              hide-default-footer
              @page-count="pageCount = $event"
            >
              <template v-slot:item.givenName="{ item }">
                {{ getFullName(item) }}
              </template>
              <template v-slot:item.role="{}">
                {{ $t(`components.employeeList.passenger`) }}
              </template>
              <template v-slot:item.groups="{ item }">
                {{ displayGroups(item.groups) }}
              </template>
              <template v-slot:item.action="{ item }"> <v-tooltip bottom >
                  <template v-slot:activator="{ on, attrs }">
                    <email-icon v-if="getActiveTab" v-on="on" v-bind="attrs" @click="resendEmailInvitation(item)" class="mr-1 icon-control-list"/>
                  </template>
                  <span>{{ $t(`components.employeeList.tooltips.resendMail`) }}</span>
                </v-tooltip>
                <v-tooltip bottom >
                  <template v-slot:activator="{ on, attrs }">
                    <edit-icon @click="editEmployeeClick(item)" v-on="on" v-bind="attrs" class="mr-1 icon-control-list"/>
                  </template>
                  <span v-if="tab === 0">{{ $t(`components.employeeList.tooltips.editEmployee`) }}</span>
                  <span v-else>{{ $t(`components.employeeList.tooltips.editGroup`) }}</span>
                </v-tooltip>
                <v-tooltip bottom >
                  <template v-slot:activator="{ on, attrs }">
                    <delete-icon @click="deleteEmployeeClick(item)" v-on="on" v-bind="attrs" v-if="!item.isDefault" class="mr-1 icon-control-list"/>
                  </template>
                  <span v-if="tab === 0">{{ $t(`components.employeeList.tooltips.deleteEmployee`) }}</span>
                  <span v-else>{{ $t(`components.employeeList.tooltips.deleteGroup`) }}</span>
                </v-tooltip>
              </template>
            </v-data-table>
            <div class="d-flex justify-between align-center py-2 px-4 bs-table-footer">
              <v-row no-gutters class="bs-per-page">
                <span>
                  {{ $t(`global.perPage`) }}
                </span>
                <span
                  v-for="item  in [10, 20, 30]"
                  :key="item"
                  class="px-1"
                  :class="{ 'active-page-count': item === options.itemsPerPage }"
                  @click="options.itemsPerPage = item"
                >
                  {{ item }}
                </span>
              </v-row>
              <v-pagination
                v-model="page"
                :length="pageCount"
                :total-visible="7"
                color="#f2f2f2"
                class="bs-pagination"
              ></v-pagination>
            </div>
          </v-col>
        </v-row>
      </div>
    </v-container>
  </loader>
</template>

<script>
import _ from 'lodash';
import { mapGetters } from 'vuex';
import { iconLoader } from '@/helpers/iconsLoader';
import EmployeeGroupForm from './EmployeeGroupForm.vue';

export default {
  name: 'EmployeeList',
  components: {
    ...iconLoader.elements,
    EmployeeGroupForm,
  },
  props: {
    employeesLoading: {
      type: Boolean,
      required: true,
    },
    employees: {
      type: Array,
      required: true,
    },
    totalEmployees: {
      type: Number,
      required: true,
    },
    noEmployeesExist: {
      type: Boolean,
    },
  },
  data() {
    return {
      screenName: 'employee-list',
      dynamicSearch: null,
      search: null,
      options: {},
      deleteDialogs: {},
      page: 1,
      pageCount: 0,
      itemsPerPage: 10,
      loading: false,
      tab: 0,
      tabs: [
        { tab: 'users', action: 'listEmployees' },
        { tab: 'groups', action: 'listEmployeeGroups' },
      ],
      isGroupCreation: false,
      selectedGroupItem: null,
    };
  },
  created() {
    this.debouncedLoad = _.debounce(this.load, 500);
  },
  watch: {
    options: {
      handler() {
        this.load();
      },
      deep: true,
    },
    tab: {
      handler() {
        this.options.page = 1;
        this.isGroupCreation = false;
        this.load();
        this.selectedGroupItem = null;
      },
    },
    $route: {
      handler(val) {
        this.tab = val.name === 'users' ? 0 : 1;
      },
      immediate: true,
    },
    getSelectedCorporationId: {
      handler() {
        this.load();
      },
    },
    totalEmployees() {
      this.options.pageCount = Math.round(this.totalEmployees / this.itemsPerPage) + 1;
    },
    dynamicSearch(val) {
      this.search = val?.length === 0 ? null : val;
      this.page = 1;

      this.debouncedLoad();
    },
  },
  computed: {
    ...mapGetters('corporations', ['getSelectedCorporationId']),
    headers() {
      if (this.getActiveTab) {
        return [
          {
            text: this.$t('components.employeeList.list.name'),
            align: 'left',
            sortable: true,
            value: 'givenName',
            class: 'text-uppercase',
          },
          {
            text: this.$t('components.employeeList.list.role'),
            align: 'left',
            sortable: true,
            value: 'role',
            class: 'text-uppercase',
          },
          {
            text: this.$t('components.employeeList.tabs.groups'),
            align: 'left',
            sortable: true,
            value: 'groups',
            class: 'text-uppercase',
          },
          {
            text: this.$t('components.employeeList.list.email'),
            align: 'left',
            sortable: true,
            value: 'email',
            class: 'text-uppercase',
          },
          {
            text: this.$t('components.employeeList.list.phone'),
            align: 'left',
            sortable: true,
            value: 'phone',
            class: 'text-uppercase',
          },
          {
            value: 'action',
            width: '15%',
            sortable: false,
            align: 'end',
          },
        ];
      }
      return [
        {
          text: this.$t('components.employeeList.list.groupName'),
          align: 'left',
          sortable: false,
          value: 'name',
          class: 'text-uppercase',
        },
        {
          text: this.$t('components.employeeList.list.type'),
          align: 'left',
          sortable: false,
          value: 'type',
          class: 'text-uppercase',
        },
        {
          value: 'action',
          width: '10%',
          sortable: false,
          align: 'end',
        },
      ];
    },
    getActiveTab() {
      return this.tab === 0;
    },
  },
  methods: {
    updateGroup() {
      this.isGroupCreation = !this.isGroupCreation;
      this.load();
    },
    addEmployeeClick() {
      if (this.getActiveTab) {
        this.$emit('addEmployeeClick');
      } else {
        this.isGroupCreation = true;
      }
    },
    async editGroup(item) {
      this.isGroupCreation = true;
      const { data } = await this.$store.dispatch('employee/getEmployeeGroup', item.id);
      this.selectedGroupItem = data;
    },
    editEmployeeClick(item) {
      // eslint-disable-next-line no-unused-expressions
      this.getActiveTab
        ? this.$emit('editEmployeeClick', item)
        : this.editGroup(item);
    },
    deleteEmployeeClick(item) {
      this.$emit(this.getActiveTab ? 'deleteEmployeeClick' : 'deleteEmployeeGroupClick', item);
    },
    async resendEmailInvitation(item) {
      this.$emit('resendEmailClick', item);
    },
    getFullName(item) {
      return `${item.givenName} ${item.familyName}`;
    },
    getTabAction() {
      return this.tabs[this.tab];
    },
    async load() {
      const {
        page, itemsPerPage, sortBy, sortDesc,
      } = this.options;
      const sort = sortBy[0] || this.getActiveTab ? 'givenName' : 'createdAt';
      this.$emit('loadData', {
        page: page - 1,
        dynamicSearch: this.search,
        maxPerPage: itemsPerPage,
        sort: sortDesc[0] ? `-${sort}` : sort,
        tab: this.getTabAction().action,
        corporationId: this.getSelectedCorporationId,
      });
    },
    displayGroups(groups) {
      if (groups) {
        return groups.map((group) => group.name).join(',');
      }
      return '';
    },
    showImportDialog() {
      this.$emit('import');
    },
    editItem(item) {
      this.$emit('editEmployeeClick', item);
    },
    deleteItem(item) {
      this.$emit('deleteEmployeeClick', item);
    },
    exportUsersList() {
      this.$emit('exportUsersList');
    },
  },
};
</script>

<style lang="scss" scoped>
.datatable-title {
  font-weight: 700;
}
.hr-line {
  height:3px;
  background-color: #000000;
}
.no-employees-container {
  flex-flow: column;
  align-content: center;
  .header-list-button {
    max-width: 250px;
    min-width: 250px !important;
    margin-bottom: 50px;
  }
  .no-employees-col {
    display: flex;
    justify-content: center;
  }
  .no-employees-icon {
    color: #247DFF;
    width: 80px;
    margin-left: 20px;
    margin-top: 50px;
  }
}
</style>
